import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useState } from 'react';
import { Bell, Check } from 'tabler-icons-react';

import {
  useGetNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from 'api/notifications';
import { Notification, NotificationType } from 'types';

dayjs.extend(relativeTime);

type NotificationCenterProps = {
  opened: boolean;
  onClose: () => void;
};

const NOTIFICATION_TYPE_COLORS: Record<NotificationType, string> = {
  mention: 'blue',
  watch: 'gray',
  assignment: 'green',
  status_change: 'yellow',
  comment: 'cyan',
  issue_created: 'indigo',
  issue_updated: 'violet',
  due_date: 'red',
  project_invite: 'teal',
  workflow_transition: 'orange',
};

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  mention: 'Mention',
  watch: 'Watched',
  assignment: 'Assignment',
  status_change: 'Status Change',
  comment: 'Comment',
  issue_created: 'Issue Created',
  issue_updated: 'Issue Updated',
  due_date: 'Due Date',
  project_invite: 'Project Invite',
  workflow_transition: 'Workflow',
};

function NotificationCenter({ opened, onClose }: NotificationCenterProps) {
  const theme = useMantineTheme();
  const { data, isLoading, refetch } = useGetNotifications(false, undefined, 50);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');

  const notifications: Notification[] = data?.notifications || [];
  const unreadCount: number = data?.unreadCount || 0;

  const filteredNotifications: Notification[] =
    filter === 'unread'
      ? notifications.filter((n: Notification) => !n.readAt)
      : notifications;

  const handleMarkRead = (notificationId: string) => {
    markRead.mutate(notificationId, {
      onSuccess: () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetch().catch(() => undefined);
      },
    });
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate(undefined, {
      onSuccess: () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetch().catch(() => undefined);
      },
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readAt) {
      handleMarkRead(notification._id);
    }

    if (notification.issue && notification.project) {
      window.location.href = `/projects/${notification.project._id}?selectedIssue=${notification.issue._id}`;
    }
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Group position="apart" style={{ width: '100%' }}>
          <Group spacing="xs">
            <Bell size={20} />
            <Text weight={600}>Notifications</Text>
            {unreadCount > 0 && (
              <Badge size="sm" color="red">
                {unreadCount}
              </Badge>
            )}
          </Group>
          {unreadCount > 0 && (
            <Button
              size="xs"
              variant="subtle"
              onClick={handleMarkAllRead}
              loading={markAllRead.isLoading}
            >
              Mark all read
            </Button>
          )}
        </Group>
      }
      styles={{
        drawer: {
          backgroundColor:
            theme.colorScheme === 'dark' ? '#1A1B1E' : theme.white,
        },
        header: {
          backgroundColor:
            theme.colorScheme === 'dark' ? '#1A1B1E' : theme.white,
          borderBottom: `1px solid ${theme.colorScheme === 'dark' ? '#2C2E33' : '#E9ECEF'
            }`,
        },
        body: {
          backgroundColor:
            theme.colorScheme === 'dark' ? '#1A1B1E' : theme.white,
        },
      }}
    >
      <Stack spacing="xs">
        <Group spacing="xs">
          <Button
            size="xs"
            variant={filter === 'all' ? 'filled' : 'subtle'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            size="xs"
            variant={filter === 'unread' ? 'filled' : 'subtle'}
            onClick={() => setFilter('unread')}
          >
            Unread
            {' '}
            {unreadCount > 0 && `(${unreadCount})`}
          </Button>
        </Group>

        <ScrollArea style={{ height: 'calc(100vh - 200px)' }}>
          {(() => {
            if (isLoading) {
              return (
                <Text color="dimmed" align="center" py="xl">
                  Loading notifications...
                </Text>
              );
            }
            if (filteredNotifications.length === 0) {
              return (
                <Text color="dimmed" align="center" py="xl">
                  {filter === 'unread'
                    ? 'No unread notifications'
                    : 'No notifications'}
                </Text>
              );
            }
            return (
              <Stack spacing="xs">
                {filteredNotifications.map((notification) => (
                  <Group
                    key={notification._id}
                    p="sm"
                    noWrap
                    sx={(currentTheme) => {
                      let backgroundColor = 'transparent';

                      if (!notification.readAt) {
                        backgroundColor =
                          currentTheme.colorScheme === 'dark'
                            ? '#25262B'
                            : '#F8F9FA';
                      }

                      return {
                        cursor: 'pointer',
                        backgroundColor,
                        borderRadius: 4,
                        border: `1px solid ${currentTheme.colorScheme === 'dark'
                            ? '#2C2E33'
                            : '#E9ECEF'
                          }`,
                      };
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <Badge
                      size="sm"
                      color={
                        NOTIFICATION_TYPE_COLORS[notification.type] || 'gray'
                      }
                      variant="light"
                    >
                      {NOTIFICATION_TYPE_LABELS[notification.type]}
                    </Badge>
                    <Stack spacing={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" lineClamp={2}>
                        {notification.message}
                      </Text>
                      {notification.issue && (
                        <Text size="xs" color="dimmed">
                          {notification.issue.jiraKey ||
                            notification.issue._id.slice(-8)}
                          :
                          {' '}
                          {notification.issue.title}
                        </Text>
                      )}
                      <Text size="xs" color="dimmed">
                        {dayjs(notification.createdAt).fromNow()}
                      </Text>
                    </Stack>
                    {!notification.readAt && (
                      <Tooltip label="Mark as read">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notification._id);
                          }}
                        >
                          <Check size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                ))}
              </Stack>
            );
          })()}
        </ScrollArea>
      </Stack>
    </Drawer>
  );
}

export default NotificationCenter;
